import React, { useState, useRef, useEffect } from 'react';

const AuthenticationSystem = () => {
  // States for different views and functionalities
  const [authMethod, setAuthMethod] = useState('email'); // 'email', 'face', 'other'
  const [currentView, setCurrentView] = useState('login'); // 'login', 'register', 'forgot'
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // null, 'success', 'fail'
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false); 
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const initialFormData = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dob: '',
    securityQuestion: '',
    securityAnswer: '',
    skills: [],
    address: '',
    profileImage: null,
    accountType: ''
  };
  
  // State for form data
  const [formData, setFormData] = useState(initialFormData);

  const ssoLoginUrl = process.env.REACT_APP_SSO_LOGIN_URL || '';
  const boardOmeRedirectUrl = process.env.REACT_APP_EduConnect_REDIRECT_URL || '';

 
  const handleSSOClick = () => {
    if (!ssoLoginUrl || !boardOmeRedirectUrl) {
        console.error("SSO URL or Redirect URL is missing!");
        return;
    }
    
    const redirectUrl = `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(boardOmeRedirectUrl)}`;
    console.log("Redirecting to:", redirectUrl); // Debugging
    window.location.href = redirectUrl;
};


  // Carousel images and content
  const carouselItems = [
    {
      image: "https://images.unsplash.com/photo-1633265486064-086b219458ec",
      title: "Secure Authentication",
      description: "State-of-the-art facial recognition to keep your account safe"
    },
    {
      image: "https://images.unsplash.com/photo-1593697972672-b1c1956b5fdb",
      title: "Instant Access",
      description: "Quick and effortless login with just your face"
    },
    {
      image: "https://images.unsplash.com/photo-1563237023-b1e970526dcb",
      title: "Advanced Technology",
      description: "Using cutting-edge AI to verify your identity"
    },
    {
      image: "https://images.unsplash.com/photo-1521747116042-5a810fda9664",
      title: "Powered by 0X10LayerFF",
      description: "Our team specializes in cybersecurity, ensuring your data is always safe and secure."
    }
  ];

  // Carousel automation
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

 
  
  const startFaceScan = async () => {
    setScanning(true);

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            setTimeout(async () => {
                // Capture a frame from the video feed
                const canvas = document.createElement("canvas");
                const video = videoRef.current;

                if (!video) {
                    alert("Camera not available");
                    setScanResult('fail');
                    setScanning(false);
                    return;
                }

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Convert canvas image to Blob
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        alert("Failed to capture image.");
                        setScanResult('fail');
                        setScanning(false);
                        return;
                    }

                    const formData = new FormData();
                    formData.append('image', blob, "face_capture.jpg");

                    // Define the base URL
                    //const baseUrl = 'http://127.0.0.1:5001'; // Set your base URL here

                    // Define the endpoint based on the current view
                    let endpoint = '/auth/login/face';                    

                    const url = `${endpoint}`; // Construct the full URL                    

                    try {
                        const response = await fetch(url, {
                            method: "POST",
                            body: formData
                        });

                        const data = await response.json();
                        //console.log(data);

                        if (response.ok && data.success) {
                            window.location.href = data.redirect; 
                            setScanResult('success');
                            setScanning(false);
                        } else {
                            setScanResult('fail');
                            setScanning(false);
                            alert(data.message || "Face authentication failed.");
                        }
                    } catch (error) {
                        setScanResult('fail');
                        setScanning(false);
                        alert("Something went wrong with Face ID authentication.");
                    }

                    stream.getTracks().forEach(track => track.stop());

                }, "image/jpeg");

            }, 3000);
        } catch (err) {
            console.error("Error accessing camera:", err);
            setScanResult('fail');
            setScanning(false);
        }
    } else {
        alert("Camera access not supported");
        setScanResult('fail');
        setScanning(false);
    }
};


// Add these state variables to your component
const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: 'gray' });
const [errors, setErrors] = useState({});
const [showCamera, setShowCamera] = useState(false);


// Security questions options
const securityQuestions = [
  "What was the name of your first pet?",
  "In what city were you born?",
  "What is your mother's maiden name?",
  "What high school did you attend?",
  "What was the make of your first car?",
  "What is your favorite movie?",
  "What is the name of the road you grew up on?",
  "What was your childhood nickname?"
];

// Skill options
const skillOptions = [
  "Programming",
  "Web Development",
  "Graphic Design",
  "Digital Marketing",
  "Data Analysis",
  "Project Management",
  "Content Writing",
  "Mobile Development",
  "UI/UX Design",
  "Social Media",
  "SEO",
  "Customer Service"
];

// Password strength checker
const checkPasswordStrength = (password) => {
  if (!password) {
    return { score: 0, message: '', color: 'gray' };
  }
  
  let score = 0;
  let message = "Weak";
  let color = "red";
  
  // Length check
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Complexity checks
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  
  // Set message and color based on score
  if (score >= 6) {
    message = "Very Strong";
    color = "green";
  } else if (score >= 4) {
    message = "Strong";
    color = "lightgreen";
  } else if (score >= 3) {
    message = "Moderate";
    color = "orange";
  }
  
  return { score, message, color };
};

// Enhanced input change handler
const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({ ...prev, [name]: value }));
  
  // Clear error when field is edited
  if (errors[name]) {
    setErrors(prev => ({ ...prev, [name]: null }));
  }
  
  // Check password strength
  if (name === 'password') {
    setPasswordStrength(checkPasswordStrength(value));
    
    // Check if confirm password matches
    if (formData.confirmPassword && formData.confirmPassword !== value) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: "Passwords don't match" 
      }));
    } else if (formData.confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: null 
      }));
    }
  }
  
  // Check if password matches confirm password
  if (name === 'confirmPassword' && formData.password !== value) {
    setErrors(prev => ({ 
      ...prev, 
      confirmPassword: "Passwords don't match" 
    }));
  } else if (name === 'confirmPassword') {
    setErrors(prev => ({ 
      ...prev, 
      confirmPassword: null 
    }));
  }
  
  // Check if security answer is provided when question is selected
  if (name === 'securityQuestion' && value && !formData.securityAnswer) {
    setErrors(prev => ({ 
      ...prev, 
      securityAnswer: "Please provide an answer to your security question" 
    }));
  }
};

// Handle skills selection (multiple select)
const handleSkillsChange = (e) => {
  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
  setFormData(prev => ({ ...prev, skills: selectedOptions }));
};

// Generate strong password
const generateStrongPassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+';
  let password = '';
  
  // Ensure at least one of each character type
  password += chars.charAt(Math.floor(Math.random() * 26)); // Uppercase
  password += chars.charAt(26 + Math.floor(Math.random() * 26)); // Lowercase
  password += chars.charAt(52 + Math.floor(Math.random() * 10)); // Number
  password += chars.charAt(62 + Math.floor(Math.random() * 14)); // Special
  
  // Add more random characters
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  // Shuffle the password
  password = password.split('').sort(() => 0.5 - Math.random()).join('');
  
  setFormData(prev => ({ 
    ...prev, 
    password, 
    confirmPassword: password 
  }));
  
  setPasswordStrength(checkPasswordStrength(password));
  
  // Clear any password-related errors
  setErrors(prev => ({ 
    ...prev, 
    password: null, 
    confirmPassword: null 
  }));
};

// Camera functions
const startCamera = () => {
  setShowCamera(true);
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      } 
    })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(err => {
        console.error("Camera error:", err);
        setErrors(prev => ({ 
          ...prev, 
          camera: "Camera access denied or not available" 
        }));
        setShowCamera(false);
      });
  } else {
    setErrors(prev => ({ 
      ...prev, 
      camera: "Your browser doesn't support camera access" 
    }));
    setShowCamera(false);
  }
};

const stopCamera = () => {
  if (videoRef.current && videoRef.current.srcObject) {
    const tracks = videoRef.current.srcObject.getTracks();
    tracks.forEach(track => track.stop());
    videoRef.current.srcObject = null;
  }
  setShowCamera(false);
};

const capturePhoto = () => {
  if (videoRef.current && canvasRef.current) {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const dataUrl = canvas.toDataURL('image/jpeg');
    setPreviewImage(dataUrl);
    
    // Create a file object from the data URL
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
        //console.log("Captured file:", file);
        setFormData(prev => ({ ...prev, profileImage: file })); // Store the image file in state
      } else {
        //console.error("Blob creation failed. Check canvas context or ensure video has frame.");
      }
    }, 'image/jpeg');

    // Stop the camera (make sure this is after blob creation)
    stopCamera();
  }
};


// Enhanced validation
const validateForm = () => {
  const newErrors = {};
  
  // Basic validation for required fields
  Object.entries(formData).forEach(([key, value]) => {
    if (['username', 'email', 'firstName', 'lastName', 'password', 'confirmPassword', 'phoneNumber'].includes(key) && !value) {
      newErrors[key] = `${key.charAt(0).toUpperCase() + key.slice(1)} is required`;
    }
  });
  
  // Email validation
  if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
    newErrors.email = "Invalid email address";
  }
  
  // Password validation
  if (formData.password && formData.password.length < 8) {
    newErrors.password = "Password must be at least 8 characters";
  }
  
  // Confirm password validation
  if (formData.password !== formData.confirmPassword) {
    newErrors.confirmPassword = "Passwords don't match";
  }
  
  // Age validation (must be 13+)
  if (formData.dob) {
    const age = new Date().getFullYear() - new Date(formData.dob).getFullYear();
    if (age < 13) {
      newErrors.dob = "You must be at least 13 years old";
    }
  }
  
  // Security question validation
  if (formData.securityQuestion && !formData.securityAnswer) {
    newErrors.securityAnswer = "Security answer is required";
  }
  
  // Phone number validation
  if (formData.phoneNumber && !/^\+?[1-9]\d{1,14}$/.test(formData.phoneNumber)) {
    newErrors.phoneNumber = "Invalid phone number format";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0; // Return true if no errors
};

   
  // Cancel face scanning
  const cancelFaceScan = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
    setScanResult(null);
    setAuthMethod('email');
  };

   // Handle file upload for registration
const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    setImageFile(file); // store the image file in state
  }
};



const handleSubmit = async (e) => {
  e.preventDefault();

  //console.log('Profile Image:', formData.profileImage);

  const validationErrors = validateForm();
  if (Object.keys(validationErrors).length > 0) {
    // Join error messages into a single string
    const errorMessages = Object.values(validationErrors).join('\n');
    alert(errorMessages); // Display the error messages
    return; // Prevent submission if there are validation errors
  }

  // Prepare form data to send to the server
  const dataToSend = new FormData();
  dataToSend.append('email', formData.email);
  dataToSend.append('firstName', formData.firstName); // Include first name
  dataToSend.append('lastName', formData.lastName); // Include last name
  dataToSend.append('password', formData.password);
  dataToSend.append('confirmPassword', formData.confirmPassword); // Add confirm password if required
  dataToSend.append('phoneNumber', formData.phoneNumber); // Include phone number
  dataToSend.append('dob', formData.dob); // Date of birth
  dataToSend.append('securityQuestion', formData.securityQuestion); // Include security question
  dataToSend.append('securityAnswer', formData.securityAnswer); // Include security answer 
  dataToSend.append('skills', formData.skills.join(',')); // Convert skills array to comma-separated string
  dataToSend.append('address', formData.address); // Include address 
   // Debugging step: Check here if profileImage is available
   if (formData.profileImage) {
    //console.log("Appending profile image:", formData.profileImage);
    dataToSend.append('profileImage', formData.profileImage);
  }
  
  dataToSend.append('accountType', formData.accountType); // Include account type

  // Define the base URL
  //const baseUrl = 'http://127.0.0.1:5001'; // Set your base URL here

  // Define the endpoint based on the current view
  let endpoint;
  if (currentView === 'register') {
    endpoint = '/api/register';
  } else if (currentView === 'forgot') {
    endpoint = '/api/forgot-password';
  } else {
    endpoint = '/auth/flogin';
  }

  const url = `${endpoint}`;

  try {
    setIsLoading(true); // Start loading

    const response = await fetch(url, {
        method: 'POST',
        body: dataToSend,
    });

    // Await the response.json() to actually get the data
    const data = await response.json(); // Expecting data back from the server
    //console.log("response", data, currentView); // Log data

    // Check if the response is OK (status code 200–299)
    if (!response.ok) {
      alert(`Error: ${data.message || 'Something went wrong.'}`); // Customize alert message
      return;
  }

    // Continue processing based on the current view
    if (currentView === 'login') {
      window.location.href = data.redirect; 
    } else if (currentView === 'register') {
        alert(data.message || "Account created successfully! Please login.");
        setCurrentView('login');
        // Reset the form and errors
        setFormData(initialFormData);
        setErrors({});
    } else if (currentView === 'forgot') {
        alert(data.message || "Password reset link sent to your email!");
        setCurrentView('login');
    }
} catch (error) {
    //console.error('There was a problem with the fetch operation:', error);
    alert("Something went wrong. Please try again.");
} finally {
    setIsLoading(false); // Stop loading
}
};




  // Render appropriate form based on current view
  const renderForm = () => {
    switch (currentView) {
      case 'register':
        return (
          <div className="w-full max-w-md space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-800">Create Account</h2>           
           
            
            <form onSubmit={handleSubmit} className="space-y-4">             
              
              {/* First name and Last name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    autoComplete='off'
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    autoComplete='off'
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>

               {/* phoneNumber and Email */}
               <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    autoComplete='off'
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    autoComplete='off'
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>
              
              {/* DOB and Account Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    autoComplete='off'
                    value={formData.dob}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.dob ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.dob && (
                    <p className="mt-1 text-sm text-red-600">{errors.dob}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Type</label>
                  <select
                    name="accountType"
                    autoComplete='off'
                    value={formData.accountType}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.accountType ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  >
                    <option value="">Select an account type</option>
                    <option value="personal">Personal</option>
                    <option value="business">Business</option>
                    <option value="professional">Professional</option>
                  </select>
                  {errors.accountType && (
                    <p className="mt-1 text-sm text-red-600">{errors.accountType}</p>
                  )}
                </div>
              </div>
              
              {/* Address and Skills */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    name="address"
                    autoComplete='off'
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Skills</label>
                  <select
                    name="skills"
                    autoComplete='off'
                    multiple
                    value={formData.skills || []}
                    onChange={handleSkillsChange}
                    className={`w-full px-3 py-0 border ${errors.skills ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    size="3"
                  >
                    {skillOptions.map(skill => (
                      <option key={skill} value={skill}>{skill}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple skills</p>
                  {errors.skills && (
                    <p className="mt-1 text-sm text-red-600">{errors.skills}</p>
                  )}
                </div>
              </div>
              
              {/* Security Question */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Security Question</label>
                  <select
                    name="securityQuestion"
                    autoComplete='off'
                    value={formData.securityQuestion || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.securityQuestion ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  >
                    <option value="">Select a security question</option>
                    {securityQuestions.map((question, index) => (
                      <option key={index} value={question}>{question}</option>
                    ))}
                  </select>
                  {errors.securityQuestion && (
                    <p className="mt-1 text-sm text-red-600">{errors.securityQuestion}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Security Answer</label>
                  <input
                    type="text"
                    name="securityAnswer"
                    autoComplete='off'
                    value={formData.securityAnswer || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.securityAnswer ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required={!!formData.securityQuestion}
                  />
                  {errors.securityAnswer && (
                    <p className="mt-1 text-sm text-red-600">{errors.securityAnswer}</p>
                  )}
                </div>
              </div>
              
              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Bio</label>
                <textarea
                  name="bio"
                  autoComplete='off'
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows="3"
                  className={`w-full px-3 py-2 border ${errors.bio ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                ></textarea>
                {errors.bio && (
                  <p className="mt-1 text-sm text-red-600">{errors.bio}</p>
                )}
              </div>
              
              {/* Password fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <button
                      type="button"
                      onClick={generateStrongPassword}
                      className="text-xs text-indigo-600 hover:text-indigo-500"
                    >
                      Generate Strong Password
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                      required
                    />
                  </div>
                  {passwordStrength.message && (
                    <div className="mt-1 flex items-center">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full" 
                          style={{ 
                            width: `${(passwordStrength.score / 6) * 100}%`,
                            backgroundColor: passwordStrength.color 
                          }}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs" style={{ color: passwordStrength.color }}>
                        {passwordStrength.message}
                      </span>
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    required
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
              
              {/* Profile Picture - with upload and face scan options */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Profile Picture</label>
                <div className="mt-1 flex items-center space-x-4">
                  <div className="flex space-x-2">
                    <label className="relative cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus-within:outline-none">
                      <span><i className="fas fa-upload mr-1"></i> Upload</span>
                      <input 
                        id="file-upload" 
                        name="file-upload" 
                        type="file" 
                        className="sr-only" 
                        onChange={handleFileUpload} 
                        accept="image/*" 
                      />
                    </label>
                    <button
                      type="button"
                      onClick={startCamera}
                      className="bg-indigo-600 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      <i className="fas fa-id-badge mr-1"></i> Face Scan
                    </button>
                  </div>
                  <div className="flex-shrink-0 h-16 w-16 border border-gray-300 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                    {previewImage ? (
                      <img src={previewImage} alt="Preview" className="h-full w-full object-cover" />
                    ) : (
                      <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                </div>
                {errors.camera && (
                  <p className="mt-1 text-sm text-red-600">{errors.camera}</p>
                )}
              </div>
              
              {/* Submit button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                  {isLoading ? (
                    <span>Creating...</span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </div>
            </form>
            <div className="text-center">
              <button onClick={() => setCurrentView('login')} className="text-sm text-indigo-600 hover:text-indigo-500">
                Already have an account? Sign in
              </button>
            </div>
            {/* CSS for the scanning animation */}
            <style>{`
              @keyframes scan {
                0% { transform: translateY(-120px); }
                50% { transform: translateY(120px); }
                100% { transform: translateY(-120px); }
              }
            `}</style>
          </div>
        );
    
     
      
      case 'forgot':
        return (
          <div className="w-full max-w-md space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-800">Reset Password</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Send Reset Link
                </button>
              </div>
            </form>
            <div className="text-center">
              <button onClick={() => setCurrentView('login')} className="text-sm text-indigo-600 hover:text-indigo-500">
                Back to Sign In
              </button>
            </div>
          </div>
        );
      
      default: // login
        return (
          <div className="w-full max-w-md space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-800">Sign In</h2>
            
            {/* Authentication Method Tabs */}
            <div className="flex rounded-md shadow-sm mb-4">
              <button
                type="button"
                onClick={() => setAuthMethod('email')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md ${
                  authMethod === 'email'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Email & Password
              </button>
              <button
                type="button"
                onClick={() => setAuthMethod('face')}
                className={`flex-1 py-2 px-4 text-sm font-medium ${
                  authMethod === 'face'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Face ID
              </button>
              <button
                type="button"
                onClick={() => setAuthMethod('other')}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md ${
                  authMethod === 'other'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Other Methods
              </button>
            </div>
            
            {/* Authentication Form */}
            {authMethod === 'email' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    autoComplete='off'
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    name="password"
                    autoComplete='off'
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentView('forgot')}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Forgot password?
                  </button>
                </div>
                <div>
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            )}
            
            {/* Face ID Authentication */}
            {authMethod === 'face' && (
              <div className="space-y-4">
                {!scanning && scanResult === null && (
                  <div className="text-center">
                    <div className="mx-auto h-24 w-24 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="h-12 w-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Use your facial features to quickly sign in to your account
                    </p>
                    <button
                      type="button"
                      onClick={startFaceScan}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Start Face Recognition
                    </button>
                  </div>
                )}
                
                {scanning && (
                  <div className="text-center">
                    <div className="relative mx-auto w-64 h-64 bg-gray-200 rounded-lg overflow-hidden">
                      <video
                        ref={videoRef}
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 border-4 border-indigo-500 animate-pulse rounded-lg" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-48 h-48 border-2 border-white rounded-full relative">
                          <div className="absolute top-0 left-1/2 w-px h-full bg-white transform -translate-x-1/2 animate-ping opacity-75"></div>
                          <div className="absolute top-1/2 left-0 w-full h-px bg-white transform -translate-y-1/2 animate-ping opacity-75"></div>
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-gray-600">
                      Scanning your face... Please keep still
                    </p>
                    <button
                      type="button"
                      onClick={cancelFaceScan}
                      className="mt-4 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {!scanning && scanResult === 'success' && (
                  <div className="text-center">
                    <div className="mx-auto h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-green-600 font-medium mb-4">Authentication successful!</p>
                    <p className="text-sm text-gray-600 mb-4">Redirecting to your dashboard...</p>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setScanResult(null)}
                        className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Continue
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setScanResult(null);
                          setAuthMethod('email');
                        }}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Try Another Method
                      </button>
                    </div>
                  </div>
                )}
                
                {!scanning && scanResult === 'fail' && (
                  <div className="text-center">
                    <div className="mx-auto h-24 w-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="text-red-600 font-medium mb-4">Authentication failed!</p>
                    <p className="text-sm text-gray-600 mb-4">We couldn't verify your identity. Please try again or use another method.</p>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setScanResult(null)}
                        className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Try Again
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setScanResult(null);
                          setAuthMethod('email');
                        }}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Use Password
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Other Authentication Methods */}
            {authMethod === 'other' && (
              <div className="space-y-4">
                <button
                  type="button"
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 0C4.477 0 0 4.477 0 10c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.866-.014-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.087.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.646 0 0 .84-.268 2.75 1.026A9.578 9.578 0 0110 4.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.026 2.747-1.026.546 1.376.203 2.394.1 2.646.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C17.14 18.163 20 14.418 20 10c0-5.523-4.477-10-10-10z" />
                  </svg>
                  Sign in with GitHub
                </button>
                <button
                  type="button"
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M19.999 10.2217C19.999 9.61842 19.9321 9.01343 19.8 8.42548H10.2V12.264H15.8437C15.615 13.551 14.8687 14.6308 13.7898 15.3082V17.8765H17.1703C19.1177 16.1028 19.9992 13.3869 19.9992 10.2222L19.999 10.2217Z" fill="#4285F4" />
                    <path d="M10.2 20C12.9529 20 15.2687 19.1112 17.1711 17.8769L13.7906 15.3086C12.8721 15.9531 11.6639 16.3264 10.2 16.3264C7.50293 16.3264 5.2174 14.5589 4.39965 12.162H0.899902V14.8109C2.7998 17.886 6.29078 20 10.2 20Z" fill="#34A853" />
                    <path d="M4.3996 12.1615C4.1998 11.5615 4.09964 10.9089 4.09964 10.2C4.09964 9.49107 4.1998 8.83847 4.3996 8.23847V5.58954H0.899902C0.326783 6.96791 0 8.54067 0 10.2C0 11.8593 0.326783 13.4321 0.899902 14.8105L4.3996 12.1615Z" fill="#FBBC05" />
                    <path d="M10.2 3.87364C11.6894 3.87364 13.0343 4.37951 14.0674 5.36234L17.0424 2.38721C15.2636 0.717605 12.9474 -0.0253181 10.2 -0.0253181C6.29078 -0.0253181 2.7998 2.08851 0.899902 5.58955L4.3996 8.23848C5.2174 5.84201 7.50293 3.87364 10.2 3.87364Z" fill="#EA4335" />
                  </svg>
                  Sign in with Google
                </button>
                <button
                  type="button"
                  onClick={handleSSOClick}
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18.8958 8.103H10.1042V11.8891H15.2354C14.8729 14.3283 12.7021 15.4183 10.1458 15.4183C7.05208 15.4183 4.58333 13.0891 4.58333 9.94578C4.58333 6.84578 6.99479 4.47328 10.1458 4.47328C11.9583 4.47328 13.3229 5.18578 14.3021 6.08995L17.1979 3.30162C15.4062 1.67328 12.9375 0.64578 10.1042 0.64578C4.89062 0.64578 0.645828 4.93578 0.645828 9.95412C0.645828 15.0558 5.01562 19.2725 10.1458 19.2725C14.9583 19.2725 19.3333 16.0225 19.3333 9.94578C19.3333 9.35828 19.0833 8.64578 18.8958 8.103Z" />
                  </svg>
                  Sign in with 0X10SSO
                </button>
              </div>
            )}
            
            <div className="text-center">
              <button 
                onClick={() => setCurrentView('register')} 
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Don't have an account? Sign up
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">

       {/* Face Scan Camera Modal */}
       {showCamera && (
              <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-lg max-w-md w-full overflow-hidden">
                  <div className="bg-indigo-600 text-white py-3 px-4 flex justify-between items-center">
                    <div className="text-xl font-bold">Face ID Scan</div>
                    <button onClick={stopCamera} className="text-white hover:text-gray-200">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="p-6">
                    <div className="text-center mb-4 text-gray-600">
                      Position your face within the frame
                    </div>
                    
                    <div className="relative mx-auto" style={{ maxWidth: "320px" }}>
                      {/* Face outline overlay */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <svg width="220" height="280" viewBox="0 0 220 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M110 20C71.4 20 40 51.4 40 90V190C40 228.6 71.4 260 110 260C148.6 260 180 228.6 180 190V90C180 51.4 148.6 20 110 20Z" 
                                stroke="#4F46E5" strokeWidth="3" strokeDasharray="10 5" fill="none" />
                          <path d="M110 40C82.4 40 60 62.4 60 90M160 90C160 62.4 137.6 40 110 40" 
                                stroke="#4F46E5" strokeWidth="2" fill="none" />
                        </svg>
                      </div>
                      
                      {/* Animated scanning line */}
                      <div className="absolute left-0 right-0 h-1 bg-indigo-500 z-10 opacity-70" 
                          style={{ 
                            top: "50%", 
                            animation: "scan 2s linear infinite" 
                          }}></div>
                      
                      {/* Camera feed with face-focused styling */}
                      <div className="rounded-xl overflow-hidden border-4 border-indigo-600" style={{ height: "320px" }}>
                        <video 
                          ref={videoRef} 
                          autoPlay 
                          playsInline 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                    
                    <div className="mt-6 text-center">
                      <button
                        type="button"
                        onClick={capturePhoto}
                        className="px-6 py-2 bg-indigo-600 rounded-md text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Capture Image
                      </button>
                    </div>
                    
                    <div className="mt-4 text-sm text-gray-500 text-center">
                      Your face will be used for identity verification
                    </div>
                  </div>
                </div>
              </div>
            )}
      {/* Left Side - Carousel */}
      <div className="hidden md:flex md:w-1/2 bg-indigo-700 p-8">
        <div className="relative w-full h-full overflow-hidden rounded-2xl">
          {/* Carousel Content */}
          <div className="h-full flex flex-col justify-center items-center text-white space-y-6 relative z-10">
            <div 
              className="absolute inset-0 transition-opacity duration-1000 ease-in-out bg-cover bg-center"
              style={{
                backgroundImage: `url('/api/placeholder/800/600')`,
                opacity: 0.3
              }}
            />
            
            {carouselItems.map((item, index) => (
              <div 
                key={index}
                className={`absolute inset-0 flex flex-col justify-center items-center p-8 transition-opacity duration-1000 ease-in-out ${
                  index === carouselIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="max-w-md flex flex-col items-center text-center space-y-6">
                  <div className="bg-white bg-opacity-10 p-6 rounded-full">
                    <svg className="h-16 w-16 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {index === 0 ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      ) : index === 1 ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      )}
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold">{item.title}</h2>
                  <p className="text-xl">{item.description}</p>
                  
                  <div className="flex space-x-3 mt-8">
                    {carouselItems.map((_, dotIndex) => (
                      <button
                        key={dotIndex}
                        className={`h-3 w-3 rounded-full transition-all ${
                          dotIndex === carouselIndex ? 'bg-white w-6' : 'bg-white bg-opacity-50'
                        }`}
                        onClick={() => setCarouselIndex(dotIndex)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Animated Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-16 -left-16 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute top-64 -right-16 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-16 left-32 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>
            
          </div>
          
        </div>
        
      </div>
      
      
      {/* Right Side - Authentication Forms */}
      <div className="w-full md:w-1/2 p-8 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
            </div>
            <h1 className="mt-4 text-center text-3xl font-extrabold text-gray-900">SecureFace</h1>
            <p className="mt-2 text-center text-sm text-gray-600">
              Advanced authentication with facial recognition
            </p>
          </div>
          
          
          {/* Render current form */}
          {renderForm()}
          <footer className="bg-gray-10 py-4">
            <div className="max-w-0xl mx-auto text-center text-gray-400">
              <p>&copy; 2025 FaceAuth. All rights reserved.</p>
              <p>&copy; Developed by Nuru The_geek</p>
              
            </div>
          </footer>
        </div>
        
      </div>
      
    </div>
    
  );
};

export default AuthenticationSystem;
